import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { uploadViaSignedUrl, type UploadProgressHandler } from './fileUpload';

// --- Types (mirror backend src/lib/ticketing.ts serializeTaskFull) ---------

import type { TaskStatusCategory } from './taskStatusesApi';

/**
 * Task statuses are configurable DB rows (CLE-DASH). `status` carries the
 * stable slug (e.g. "in_progress"); ids/labels/colors ride along on the task.
 */
export type TaskStatus = string;
export type TaskPriority = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type TaskRelationType = 'BLOCKS' | 'BLOCKED_BY' | 'RELATED' | 'DUPLICATE';

export interface Label {
  id: string;
  name: string;
  color: string;
}

export interface Task {
  id: string;
  identifier: string | null;
  /** Signature for shared links; absent in Test Mode data. */
  previewToken?: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  statusId: string;
  statusLabel: string;
  statusColor: string;
  statusCategory: TaskStatusCategory;
  statusOrder: number;
  departmentId: string | null;
  department: { id: string; name: string; color: string } | null;
  serviceId: string | null;
  service: { id: string; name: string; color: string | null; category: string } | null;
  priority: TaskPriority;
  priorityValue: number;
  estimate: number | null;
  /** Total hours logged on the task (worklog sum), null when none. */
  spentHours: number | null;
  dueDate: string | null;
  // CLE-167: every task belongs to a project, so projectId is always present.
  projectId: string;
  projectName: string | null;
  // True when the task sits in its client's own container (no real project).
  projectIsClientDefault?: boolean;
  // The client of the task's project, when the project has one.
  client?: TaskClientRef | null;
  // Set when the task is a social-media post (calendar de postări).
  socialPost?: {
    platform: string;
    publishAt: string;
    approvalStatus: string;
  } | null;
  assigneeId: string | null;
  assignee: { id: string; name: string } | null;
  parentId: string | null;
  labels: Label[];
  subIssueCount: number;
  subIssueDoneCount: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

// CLE-183: comments may carry inline image/file attachments.
export interface CommentAttachment {
  url: string;
  name: string;
  contentType: string;
}

export interface TaskComment {
  id: string;
  taskId: string;
  authorId: string | null;
  author: { id: string; name: string } | null;
  content: string;
  attachments: CommentAttachment[];
  createdAt: string;
  updatedAt: string;
}

export interface TaskActivity {
  id: string;
  taskId: string;
  userId: string | null;
  user: { id: string; name: string } | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  // Readable snapshot of the values (label name, comment text…), taken when
  // the change happened.
  oldDisplay: string | null;
  newDisplay: string | null;
  createdAt: string;
}

export type TimelineItem =
  | { kind: 'comment'; at: string; data: TaskComment }
  | { kind: 'activity'; at: string; data: TaskActivity };

export interface RelatedTaskSummary {
  id: string;
  identifier: string | null;
  title: string;
  status: TaskStatus;
  statusLabel: string;
  statusColor: string;
  statusCategory: TaskStatusCategory;
  // Enough to draw the shared task row.
  priority: TaskPriority;
  dueDate: string | null;
  projectName: string | null;
  assignee: string | null;
}

export interface TaskRelation {
  id: string;
  type: TaskRelationType;
  fromTaskId: string;
  toTaskId: string;
  toTask: RelatedTaskSummary | null;
  fromTask: RelatedTaskSummary | null;
  createdAt: string;
}

export interface TaskRelationsResult {
  relations: TaskRelation[];
  isBlocked: boolean;
  blockedBy: RelatedTaskSummary[];
}

export interface EstimateTotals {
  total: { hours: number; tasks: number };
  byStatus: Array<{
    status: TaskStatus;
    statusId: string;
    statusLabel: string;
    statusColor: string;
    statusOrder: number;
    hours: number;
    tasks: number;
  }>;
  byAssignee: Array<{ assigneeId: string | null; hours: number; tasks: number }>;
  byProject: Array<{ projectId: string | null; hours: number; tasks: number }>;
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface TaskListResult {
  tasks: Task[];
  pagination: Pagination;
}

interface Envelope<T> {
  success: true;
  data: T;
}

// Attach the current access token from the auth store to every request.
function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface TaskClientRef {
  id: string;
  name: string;
  shortName: string | null;
  color: string | null;
}

export interface TaskFilters {
  // status/priority/label accept either a single value or a list (joined as CSV).
  // Status tokens are slugs or ids (legacy uppercase enum values still resolve).
  status?: string | string[];
  priority?: TaskPriority | TaskPriority[];
  label?: string | string[];
  departmentId?: string | string[];
  serviceId?: string | string[];
  assigneeId?: string;
  projectId?: string;
  // One or more client ids: tasks in any project of those clients.
  clientId?: string | string[];
  parentId?: string;
  topLevel?: boolean;
  // Date-range filters (YYYY-MM-DD) for reports/statistics.
  dueFrom?: string;
  dueTo?: string;
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  sort?: 'priority' | 'dueDate' | 'status' | 'estimate' | 'createdAt' | 'updatedAt';
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export async function listTasks(filters: TaskFilters = {}): Promise<TaskListResult> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const qs = params.toString();
  const res = await apiFetch<Envelope<TaskListResult>>(`/api/tasks${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Personal tasks ("Sarcini personale") ----------------------------------

export interface PersonalProject {
  id: string;
  code: string;
  name: string;
}

/** Fetch (lazily creating) the caller's hidden personal-tasks project. */
export async function getPersonalProject(): Promise<PersonalProject> {
  const res = await apiFetch<Envelope<PersonalProject>>('/api/tasks/personal-project', {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Tasks without a project ("Fără proiect") ------------------------------

/** The company's shared container for tasks filed under no real project. */
export type GeneralProject = PersonalProject;

/** Fetch (lazily creating) the "Fără proiect" container project. */
export async function getGeneralProject(): Promise<GeneralProject> {
  const res = await apiFetch<Envelope<GeneralProject>>('/api/tasks/general-project', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getTask(id: string): Promise<Task> {
  const res = await apiFetch<Envelope<Task>>(`/api/tasks/${id}`, { headers: authHeaders() });
  return res.data;
}

export interface CreateTaskInput {
  /**
   * Omit on create to let the server write it: it sends the description to
   * Haiku, which returns a title and a tidied description (web create form).
   */
  title?: string;
  description?: string;
  /** Configurable status: id or slug. Omit to use the default status. */
  statusId?: string;
  departmentId?: string | null;
  serviceId?: string | null;
  priority?: TaskPriority;
  // CLE-167: every task must belong to a project — projectId is required.
  projectId: string;
  assigneeId?: string | null;
  parentId?: string | null;
  estimate?: number | null;
  dueDate?: string | null;
  labelIds?: string[];
}

export async function createTask(input: CreateTaskInput): Promise<Task> {
  const res = await apiFetch<Envelope<Task>>('/api/tasks', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export interface DuplicateTaskInput {
  /** Target project; defaults to the task's own. */
  projectId?: string;
  /** New title; defaults to "<original> (copie)". */
  title?: string;
  /** Also copy the direct sub-issues under the new task. */
  includeSubtasks?: boolean;
}

/** POST /api/tasks/:id/duplicate — a new task with its own identifier. */
export async function duplicateTask(
  id: string,
  input: DuplicateTaskInput,
): Promise<Task & { duplicatedSubtasks: number }> {
  const res = await apiFetch<Envelope<Task & { duplicatedSubtasks: number }>>(
    `/api/tasks/${encodeURIComponent(id)}/duplicate`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export type UpdateTaskInput = Partial<CreateTaskInput>;

export async function updateTask(id: string, input: UpdateTaskInput): Promise<Task> {
  const res = await apiFetch<Envelope<Task>>(`/api/tasks/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteTask(id: string): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(`/api/tasks/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

// --- Estimate totals -------------------------------------------------------

// The totals endpoint accepts the same narrowing filters as the list (CLE-435),
// so the header recomputes when a responsible/status/priority/etc. is selected.
// Pagination and sort don't affect an aggregate, so they're intentionally omitted.
export type EstimateTotalsFilters = Omit<
  TaskFilters,
  'sort' | 'order' | 'page' | 'pageSize' | 'parentId' | 'topLevel'
>;

export async function getEstimateTotals(
  filters: EstimateTotalsFilters = {},
): Promise<EstimateTotals> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const qs = params.toString();
  const res = await apiFetch<Envelope<EstimateTotals>>(`/api/tasks/totals${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Sub-issues ------------------------------------------------------------

export interface SubtasksResult {
  tasks: Task[];
  progress: { done: number; total: number };
}

export async function listSubtasks(taskId: string): Promise<SubtasksResult> {
  const res = await apiFetch<Envelope<SubtasksResult>>(`/api/tasks/${taskId}/subtasks`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Comments --------------------------------------------------------------

export async function listComments(taskId: string): Promise<TaskComment[]> {
  const res = await apiFetch<Envelope<{ comments: TaskComment[] }>>(
    `/api/tasks/${taskId}/comments`,
    { headers: authHeaders() },
  );
  return res.data.comments;
}

export async function addComment(
  taskId: string,
  content: string,
  attachments?: CommentAttachment[],
): Promise<{ comment: TaskComment; mentioned: Array<{ id: string; name: string }> }> {
  const res = await apiFetch<
    Envelope<{ comment: TaskComment; mentioned: Array<{ id: string; name: string }> }>
  >(`/api/tasks/${taskId}/comments`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      content,
      ...(attachments && attachments.length ? { attachments } : {}),
    }),
  });
  return res.data;
}

// CLE-183: edit a comment (content and/or attachments). Pass `attachments: null`
// to clear them.
export async function updateComment(
  taskId: string,
  commentId: string,
  input: { content?: string; attachments?: CommentAttachment[] | null },
): Promise<TaskComment> {
  const res = await apiFetch<Envelope<{ comment: TaskComment }>>(
    `/api/tasks/${taskId}/comments/${commentId}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data.comment;
}

/**
 * Remove one file from a comment. `name` guards against a list that changed
 * meanwhile; a comment left with no text and no files is deleted outright
 * (`deleted: true` then).
 */
export async function deleteCommentAttachment(
  taskId: string,
  commentId: string,
  index: number,
  name: string,
): Promise<{ deleted?: boolean; comment?: TaskComment }> {
  const res = await apiFetch<Envelope<{ deleted?: boolean; comment?: TaskComment }>>(
    `/api/tasks/${taskId}/comments/${commentId}/attachments/${index}?name=${encodeURIComponent(name)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

export async function deleteComment(
  taskId: string,
  commentId: string,
): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(
    `/api/tasks/${taskId}/comments/${commentId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

// --- Time entries (worklog) --------------------------------------------------

export interface TimeEntry {
  id: string;
  taskId: string;
  userId: string;
  user: { id: string; name: string } | null;
  /** Calendar day the work happened on (YYYY-MM-DD). */
  date: string;
  hours: number;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TimeEntriesResult {
  entries: TimeEntry[];
  totalHours: number;
}

export async function listTimeEntries(taskId: string): Promise<TimeEntriesResult> {
  const res = await apiFetch<Envelope<TimeEntriesResult>>(`/api/tasks/${taskId}/time-entries`, {
    headers: authHeaders(),
  });
  return res.data;
}

export interface AddTimeEntryInput {
  hours: number;
  /** YYYY-MM-DD; defaults to today on the backend. */
  date?: string;
  note?: string;
}

export async function addTimeEntry(taskId: string, input: AddTimeEntryInput): Promise<TimeEntry> {
  const res = await apiFetch<Envelope<{ entry: TimeEntry }>>(`/api/tasks/${taskId}/time-entries`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.entry;
}

export async function updateTimeEntry(
  taskId: string,
  entryId: string,
  input: { hours?: number; date?: string; note?: string | null },
): Promise<TimeEntry> {
  const res = await apiFetch<Envelope<{ entry: TimeEntry }>>(
    `/api/tasks/${taskId}/time-entries/${entryId}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data.entry;
}

export async function deleteTimeEntry(
  taskId: string,
  entryId: string,
): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(
    `/api/tasks/${taskId}/time-entries/${entryId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

// --- Activity & timeline ---------------------------------------------------

export async function listActivity(taskId: string): Promise<TaskActivity[]> {
  const res = await apiFetch<Envelope<{ activity: TaskActivity[] }>>(
    `/api/tasks/${taskId}/activity`,
    { headers: authHeaders() },
  );
  return res.data.activity;
}

/**
 * Turn a human tag (AIWS-058) into a task id, including a tag the task no
 * longer carries.
 *
 * Moving a task to another project re-issues its identifier, so a link shared
 * under the old tag would 404. The server reads the rename out of the activity
 * log; `renamed` says the tag in the URL is historical, so the caller can
 * rewrite the address bar to the current one.
 */
export async function resolveTaskIdentifier(
  identifier: string,
): Promise<{ id: string; identifier: string | null; renamed: boolean }> {
  const res = await apiFetch<Envelope<{ id: string; identifier: string | null; renamed: boolean }>>(
    `/api/tasks/resolve/${encodeURIComponent(identifier)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getTimeline(taskId: string): Promise<TimelineItem[]> {
  const res = await apiFetch<Envelope<{ timeline: TimelineItem[] }>>(
    `/api/tasks/${taskId}/timeline`,
    { headers: authHeaders() },
  );
  return res.data.timeline;
}

// --- Relations -------------------------------------------------------------

export async function getRelations(taskId: string): Promise<TaskRelationsResult> {
  const res = await apiFetch<Envelope<TaskRelationsResult>>(`/api/tasks/${taskId}/relations`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function addRelation(
  taskId: string,
  toTaskId: string,
  type: TaskRelationType,
): Promise<TaskRelation> {
  const res = await apiFetch<Envelope<TaskRelation>>(`/api/tasks/${taskId}/relations`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ toTaskId, type }),
  });
  return res.data;
}

export async function deleteRelation(
  taskId: string,
  relationId: string,
): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(
    `/api/tasks/${taskId}/relations/${relationId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

// --- Task labels (CLE-183) -------------------------------------------------

export async function addTaskLabel(taskId: string, labelId: string): Promise<Task> {
  const res = await apiFetch<Envelope<Task>>(`/api/tasks/${taskId}/labels`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ labelId }),
  });
  return res.data;
}

export async function removeTaskLabel(taskId: string, labelId: string): Promise<Task> {
  const res = await apiFetch<Envelope<Task>>(`/api/tasks/${taskId}/labels/${labelId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

// --- Image / file upload (CLE-183) -----------------------------------------

export interface UploadUrlResult {
  uploadUrl: string;
  key: string;
  publicUrl: string;
  expiresIn: number;
}

// Ask the backend for a presigned PUT URL for a task attachment.
export async function requestUploadUrl(
  taskId: string,
  filename: string,
  contentType: string,
): Promise<UploadUrlResult> {
  const res = await apiFetch<Envelope<UploadUrlResult>>(`/api/tasks/${taskId}/upload-url`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ filename, contentType }),
  });
  return res.data;
}

/**
 * Upload a file for a task: request a signed URL, PUT the bytes to storage, and
 * return the attachment to embed in markdown or attach to a comment.
 */
export async function uploadTaskFile(
  taskId: string,
  file: File,
  onProgress?: UploadProgressHandler,
): Promise<CommentAttachment> {
  return uploadViaSignedUrl(
    file,
    (filename, contentType) => requestUploadUrl(taskId, filename, contentType),
    onProgress,
  );
}

// --- AI task draft ----------------------------------------------------------
//
// Draft-in-form flow: the prompt goes to POST /api/tasks/ai-draft, the returned
// draft pre-fills the create form and the user reviews/saves through the normal
// createTask flow. Deliberately excludes status/department/service/assignee.

export interface TaskDraft {
  title: string;
  description: string | null;
  priority: TaskPriority | null;
  dueDate: string | null;
  estimate: number | null;
}

export async function draftTaskWithAi(input: {
  prompt: string;
  projectId?: string;
}): Promise<TaskDraft> {
  const res = await apiFetch<Envelope<{ draft: TaskDraft }>>('/api/tasks/ai-draft', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.draft;
}

// --- Bulk actions ----------------------------------------------------------

export type BulkAction =
  | 'status'
  | 'priority'
  | 'assignee'
  | 'project'
  | 'service'
  | 'addLabels'
  | 'removeLabels'
  | 'dueDate'
  | 'delete';

export async function bulkUpdateTasks(
  ids: string[],
  action: BulkAction,
  value?: unknown,
): Promise<{ affected: number }> {
  const res = await apiFetch<Envelope<{ affected: number }>>('/api/tasks/bulk', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ids, action, value }),
  });
  return res.data;
}
